<template>
  <div class="shrink-0">
    <DropdownMenu v-if="!readonly">
      <DropdownMenuTrigger as-child>
        <Button variant="outline" size="sm" type="button">
          Add Feature
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" class="w-56">
        <DropdownMenuLabel>Enable Feature:</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuSub>
          <DropdownMenuSubTrigger :disabled="isUpdatingDeadline">
            Deadline
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent class="w-48">
            <DropdownMenuItem :disabled="isUpdatingDeadline" @select="setPresetDeadline('today')">
              Today
            </DropdownMenuItem>
            <DropdownMenuItem :disabled="isUpdatingDeadline" @select="setPresetDeadline('tomorrow')">
              Tomorrow
            </DropdownMenuItem>
            <DropdownMenuItem :disabled="isUpdatingDeadline" @select="setPresetDeadline('end_of_week')">
              End of Week
            </DropdownMenuItem>
            <DropdownMenuItem :disabled="isUpdatingDeadline" @select="setPresetDeadline('next_week')">
              Next Week
            </DropdownMenuItem>
            <DropdownMenuItem :disabled="isUpdatingDeadline" @select="openCustomDeadlineModal">
              Custom
            </DropdownMenuItem>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger :disabled="isUpdatingPriority">
            Priority
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent class="w-40">
            <DropdownMenuItem :disabled="isUpdatingPriority || normalizedPriority === 'low'" @select="setPriority('low')">
              Low
            </DropdownMenuItem>
            <DropdownMenuItem :disabled="isUpdatingPriority || normalizedPriority === 'standard'" @select="setPriority('standard')">
              Standard
            </DropdownMenuItem>
            <DropdownMenuItem :disabled="isUpdatingPriority || normalizedPriority === 'high'" @select="setPriority('high')">
              High
            </DropdownMenuItem>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuItem :disabled="showStartAtField" @select="enableFeature('startAt')">
          Start Date
        </DropdownMenuItem>
        <DropdownMenuItem :disabled="showAssigneeField" @select="enableFeature('assignee')">
          Assignment
        </DropdownMenuItem>
        <DropdownMenuItem :disabled="showReviewProcessField" @select="enableFeature('review')">
          Review
        </DropdownMenuItem>
        <DropdownMenuItem :disabled="hasChecklistFeature || isCreatingChecklist" @select="emit('create-checklist')">
          Checklist
        </DropdownMenuItem>
        <DropdownMenuItem :disabled="showTagsField" @select="enableFeature('tags')">
          Tags
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>

    <TaskCustomDeadlineModal
      ref="customDeadlineModal"
      :task-id="taskId"
      :value="task.deadline_at"
      :context="context"
    />
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { toast } from 'vue-sonner'
import { Button } from '../../../../../../../resources/js/components/ui/button/index.js'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger } from '../../../../../../../resources/js/components/ui/dropdown-menu/index.js'
import TaskCustomDeadlineModal from './fields/TaskCustomDeadlineModal.vue'
import { useTasksStore } from '../../stores/tasksStore.js'
import { buildTaskPresetDate } from '../../utils/taskFieldDateTime.js'

const emit = defineEmits(['create-checklist', 'feature-disabled', 'feature-enabled'])

const props = defineProps({
  task: {
    type: Object,
    required: true,
  },
  context: {
    type: String,
    default: 'task-show',
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  isCreatingChecklist: {
    type: Boolean,
    default: false,
  },
})

const enabledFeatures = ref([])
const customDeadlineModal = ref(null)
const tasksStore = useTasksStore(props.context)
const taskId = computed(() => Number(props.task?.id))

const normalizedPriority = computed(() => {
  if (typeof props.task?.priority === 'string') {
    return props.task.priority.trim()
  }

  if (props.task?.priority && typeof props.task.priority === 'object' && 'value' in props.task.priority) {
    return String(props.task.priority.value).trim()
  }

  return ''
})
const hasStartAt = computed(() => hasValue(props.task?.start_at))
const hasAssignee = computed(() => {
  const assigneeId = Number(props.task?.assigned_to_user_id)

  return Number.isInteger(assigneeId) && assigneeId > 0
})
const hasReviewProcess = computed(() => {
  const reviewProcessId = Number(props.task?.review_process?.id ?? props.task?.reviewProcess?.id ?? 0)

  return Number.isInteger(reviewProcessId) && reviewProcessId > 0
})
const hasChecklistFeature = computed(() => Number(props.task?.cached?.checklists_count ?? 0) > 0)
const hasTagsFeature = computed(() => Number(props.task?.cached?.tags_count ?? 0) > 0)
const isUpdatingPriority = computed(() => tasksStore.isUpdatingPriority(taskId.value))
const isUpdatingDeadline = computed(() => tasksStore.isUpdatingDeadline(taskId.value))
const showStartAtField = computed(() => hasStartAt.value || enabledFeatures.value.includes('startAt'))
const showAssigneeField = computed(() => hasAssignee.value || enabledFeatures.value.includes('assignee'))
const showReviewProcessField = computed(() => hasReviewProcess.value || enabledFeatures.value.includes('review'))
const showTagsField = computed(() => hasTagsFeature.value || enabledFeatures.value.includes('tags'))

const enableFeature = (feature) => {
  if (!enabledFeatures.value.includes(feature)) {
    enabledFeatures.value = [...enabledFeatures.value, feature]
  }

  emit('feature-enabled', feature)
}

const disableFeature = (feature) => {
  enabledFeatures.value = enabledFeatures.value.filter((enabledFeature) => enabledFeature !== feature)
  emit('feature-disabled', feature)
}

const updateTaskDeadline = async (deadlineAt, successMessage = 'Deadline updated.') => {
  if (!Number.isInteger(taskId.value) || taskId.value <= 0) {
    return false
  }

  try {
    await tasksStore.updateDeadline(taskId.value, deadlineAt)
    enableFeature('deadline')
    toast.success(successMessage)
    return true
  } catch (error) {
    const errorMessage = error?.response?.data?.message
      || error?.response?.data?.errors?.deadline_at?.[0]
      || 'Unable to update the deadline.'
    toast.error(errorMessage)
    return false
  }
}

const setPresetDeadline = async (preset) => {
  const presetDate = buildTaskPresetDate(preset, preset === 'next_week' ? 9 : 17)

  if (!presetDate) {
    return
  }

  const successMessagesByPreset = {
    today: 'Deadline set to today.',
    tomorrow: 'Deadline set to tomorrow.',
    end_of_week: 'Deadline set to end of week.',
    next_week: 'Deadline set to next week.',
  }

  await updateTaskDeadline(
    presetDate.toISOString(),
    successMessagesByPreset[preset] ?? 'Deadline updated.'
  )
}

const openCustomDeadlineModal = () => {
  enableFeature('deadline')
  customDeadlineModal.value?.openModal?.()
}

const setPriority = async (priority) => {
  if (!Number.isInteger(taskId.value) || taskId.value <= 0 || normalizedPriority.value === priority) {
    return
  }

  try {
    await tasksStore.updatePriority(taskId.value, priority)

    if (priority === 'standard') {
      disableFeature('priority')
    } else {
      enableFeature('priority')
    }

    toast.success(`Priority set to ${PRIORITY_LABELS[priority]}.`)
  } catch (error) {
    const errorMessage = error?.response?.data?.message
      || error?.response?.data?.errors?.priority?.[0]
      || 'Unable to update the priority.'
    toast.error(errorMessage)
  }
}

const PRIORITY_LABELS = {
  low: 'Low',
  standard: 'Standard',
  high: 'High',
}

const hasValue = (value) => {
  if (value === null || value === undefined) {
    return false
  }

  if (typeof value === 'string') {
    return value.trim() !== ''
  }

  return true
}

watch(
  () => props.task?.id,
  () => {
    enabledFeatures.value = []
  },
)
</script>
